import { AsyncPipe, NgIf } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ProjectInfrastructureService } from '@app/shared/services/project-infrastructure.service';
import { UserService } from '@app/shared/services/user.service';
import { AuthService } from '@auth0/auth0-angular';
import { MeetingRegistrant, PastMeetingSummary, Permission, PutPastMeetingSummary, User } from 'lfx-pcc';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { SkeletonModule } from 'primeng/skeleton';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'lfx-meeting-summary-form',
  standalone: true,
  imports: [ButtonModule, EditorModule, ReactiveFormsModule, NgIf, SkeletonModule, AsyncPipe],
  templateUrl: './meeting-summary-form.component.html',
  styleUrls: ['./meeting-summary-form.component.scss'],
})
export class MeetingSummaryFormComponent implements OnInit {
  @Input({ required: true }) public meeting: PastMeetingSummary;
  @Input({ required: true }) public form: FormGroup;
  @Input() public editMode = false;

  @Output() public readonly refresh: EventEmitter<boolean> = new EventEmitter();

  public formLoading = false;
  public permissions$: Observable<Permission>;
  public skipAuth0: WritableSignal<boolean> = signal(false);
  public user$: Observable<User | null>;
  public user: User | null | undefined;
  public password: string = '';
  public registrant$: Observable<MeetingRegistrant | null | undefined>;
  public registrant: MeetingRegistrant | null | undefined;

  private pisService: ProjectInfrastructureService = inject(ProjectInfrastructureService);
  private messageService: MessageService = inject(MessageService);
  private userService: UserService = inject(UserService);
  private authService = inject(AuthService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  public ngOnInit() {
    this.skipAuth0 = this.userService.skipAuth0;
    this.password = this.activatedRoute.snapshot.queryParams.password || '';

    this.user$ = this.authService.isLoading$.pipe(
      switchMap((isLoading) => {
        if (isLoading) {
          return of(false);
        }

        return this.authService
          .getAccessTokenSilently({
            detailedResponse: true,
          })
          .pipe(
            catchError((e) => {
              console.info('User is not authenticated - ', e.message);
              return of(false);
            })
          );
      }),
      switchMap((user) => {
        if (user) {
          const params = new HttpParams({
            fromObject: {
              basic: true,
            },
          });
          return this.userService.getMe(params);
        }

        return of({});
      }),
      tap((user) => {
        this.user = user;
        if (this.user) {
          this.permissions$ = this.userService.getProjectPermissions(this.meeting.project_id).pipe(
            take(1),
            tap((permissions) => {
              this.userService.permissions = permissions;
            })
          );

          const params = new HttpParams({ fromObject: { username: this.user.Username } });
          this.registrant$ = this.pisService.getPastMeetingRegistrantByUserId(this.meeting.meeting_id, this.user.ID, params).pipe(
            take(1),
            tap((registrant) => {
              this.registrant = registrant;
            }),
            catchError((error) => {
              console.error('Error fetching registrant details:', error);
              // Set registrant as an object with host: false
              this.registrant = { host: false } as any;
              return of(this.registrant);
            })
          );
        }
      })
    );
  }

  public get canManage(): boolean {
    return this.userService.permissions?.v2_zoom_meeting?.update || !!this.registrant?.host;
  }

  public get showApproveCTA(): boolean {
    return !this.form.value.approved && this.form.value.requires_approval;
  }

  public onSave(publish: boolean = false) {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.formLoading = true;
      const payload: PutPastMeetingSummary = {
        approved: publish,
        edited_next_steps: (this.form.value.nextSteps ?? '')
          .split('<br>')
          .map((step: string) => step.trim())
          .filter(Boolean),
        edited_summary_details: [
          {
            label: this.meeting?.summary_details?.[0]?.label ?? 'Summary',
            summary: this.form.value.summary?.trim() ?? '',
          },
        ],
        edited_summary_overview: this.form.value.recap,
      };
      if (this.meeting?.past_meeting_id && this.meeting?.id) {
        const params = new HttpParams({ fromObject: { project: this.meeting.project_id, slug: this.meeting.project_slug } });
        this.pisService
          .putPastMeetingSummary(this.meeting.past_meeting_id, this.meeting.id, payload, params)
          .pipe(take(1))
          .subscribe(
            () => {
              this.formLoading = false;
              this.editMode = false;
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Meeting summary updated successfully',
              });
              this.refresh.emit(true);
            },
            (error) => {
              console.error(error);
              this.formLoading = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update meeting summary',
              });
            }
          );
      } else {
        this.formLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Missing required meeting information',
        });
      }
    }
  }

  public switchEditMode(): void {
    this.editMode = !this.editMode;
  }

  public onLogin(createAccount: boolean = false): void {
    const meetingData: { [key: string]: string } = {
      meetingID: this.meeting.past_meeting_id,
    };

    if (createAccount) {
      meetingData.signup = 'true';
    }

    if (this.password) {
      meetingData.password = this.password;
    }

    localStorage.setItem('meetingData', JSON.stringify(meetingData));

    this.authService.loginWithRedirect({
      authorizationParams: {
        ...meetingData,
        prompt: 'login',
        screen_hint: createAccount ? 'signup' : 'login',
      },
      appState: {
        ...meetingData,
        subResource: `summaries`,
      },
    });
  }
}
